import React, { useCallback } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Logger from 'js-logger';
import _get from 'lodash/get';
import { hooks } from '@lifesize/clients.sdk';
import { VideoStream } from '@lifesize/clients.sdk/dist/components';
import { makeStyles, Box, Theme } from '@material-ui/core';
import { ReactComponent as BackIcon } from '@lifesize/ux-assets/vectors/svgIcons/chevron-back.svg';
import colors from '../../styles/variables/baseColors.module.scss'; // bridge from scss variables to js
import ImagePicker from 'components/ImagePicker/ImagePicker';
import VbbDisclaimer from 'components/VbbSettings/VbbDisclaimer';
import { bgSelectionType } from 'reducers/vbbSettingsSlice';
import { vbbManager } from 'utils/vbbManager';
import { defaultVideoAspect } from 'utils/videoResolution';

const messages = defineMessages({
  btnBack: { id: 'components.vbbSettings.btnBack', defaultMessage: 'Back' }
});

interface Props {
  intl: { formatMessage: Function };
  isJoinFlowCall?: boolean;
  isStartCallFlow?: boolean;
  onToggleVbbSettings: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const VbbSettings = (props: Props) => {
  const classes = useStyles({});
  const dispatch = useDispatch();
  const { intl, onToggleVbbSettings } = props;
  const { enabled: vbbEnabled, selection: bgSelection } = useSelector((state) => _get(state, 'app.vbbSettings'));
  const { localVideoMuted } = hooks.useMedia();
  const localPrimaryStream = hooks.useLocalPrimaryStream();
  const mediaSettings = useSelector((state) => _get(state, 'app.mediaSettings'));

  const onSelectBg = useCallback(
    (newBgSelection: bgSelectionType, saveAsDefault: boolean) => {
      if (newBgSelection.id === bgSelection.id) {
        Logger.warn('Chose same background as existing setting; not calling vbbManager.', bgSelection.id);
        return;
      }

      vbbManager.chooseBackgroundSetting(
        dispatch,
        mediaSettings,
        localVideoMuted,
        vbbEnabled,
        newBgSelection,
        saveAsDefault
      );
    },
    [dispatch, localVideoMuted, vbbEnabled, bgSelection, mediaSettings]
  );

  return (
    <Box className={classes.container}>
      <section className={classes.header}>
        <a onClick={onToggleVbbSettings} className={classes.backBtn}>
          <BackIcon />
          <label>{intl.formatMessage(messages.btnBack)}</label>
        </a>
      </section>
      <section className={classes.preview}>
        <Box className={classes.videoPreview}>
          <VideoStream
            elementId="vbbPreview"
            className={classes.videoMirror}
            muted={true}
            mediaStream={localPrimaryStream.stream}
          />
        </Box>
        <Box className={classes.previewInfo}>
          <VbbDisclaimer />
        </Box>
      </section>
      <section className={classes.picker}>
        <ImagePicker
          onSelect={onSelectBg}
          isJoinFlowCall={props.isJoinFlowCall}
          isStartCallFlow={props.isStartCallFlow}
        />
      </section>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    height: '100%',
    maxWidth: 700,
    overflow: 'hidden',
    width: '100%'
  },
  header: {
    display: 'flex',
    flex: '0 0 auto',
    marginBottom: theme.spacing(3)
  },
  backBtn: {
    color: colors.chambray,
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 12,

    '& svg': {
      height: 16,
      marginRight: 8,
      width: 'auto'
    },
    '& label': {
      cursor: 'inherit',
      lineHeight: 1,
      marginTop: '0.25em'
    }
  },
  preview: {
    display: 'flex',
    flex: '0 0 auto',
    width: '100%'
  },
  videoPreview: {
    flex: '0 0 auto',
    paddingTop: `calc(50% * ${defaultVideoAspect})`, // magic to maintain aspect ratio
    position: 'relative',
    width: '50%',
    '& video': {
      bottom: 0,
      height: '100%',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: '100%'
    }
  },
  videoMirror: {
    transform: 'rotateY(180deg)'
  },
  previewInfo: {
    flex: '0 0 auto',
    paddingLeft: theme.spacing(2),
    width: '50%'
  },
  picker: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    marginTop: theme.spacing(2)
  }
}));

export default injectIntl(VbbSettings);
